import { useEffect, useRef, useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { todayISO } from '../../utils/datetime';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const short = (iso) => {
  if (!iso) return '';
  const [year, month, day] = iso.split('-');
  return `${Number(day)} ${MONTHS[Number(month) - 1]} ${year}`;
};

const DateRangeFilter = ({ from = '', to = '', onFromChange, onToChange, label = 'Date', className = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const max = todayISO();
  const active = Boolean(from || to);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const clear = () => {
    onFromChange('');
    onToChange('');
  };

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={[
          'inline-flex items-center gap-1.5 rounded border px-2 py-1 text-sm transition-colors',
          active
            ? 'border-primary-500 text-primary-600 bg-primary-500/5'
            : 'border-theme-border text-theme-bodycolor hover:border-primary-300'
        ].join(' ')}
      >
        <Calendar size={13} />
        <span className="whitespace-nowrap">
          {active ? `${short(from) || 'Any'} – ${short(to) || 'Any'}` : label}
        </span>
        <ChevronDown size={12} />
      </button>

      <div
        hidden={!open}
        className="absolute left-0 top-full z-20 mt-1 w-56 rounded bg-theme-cardbg p-3 shadow-[0_4px_24px_0_rgba(62,57,107,.18)]"
      >
        <label className="mb-2 block">
          <span className="mb-1 block text-sm text-theme-secondarytextcolor">From</span>
          <input
            type="date"
            className="form-control form-control-sm"
            value={from}
            max={to || max}
            onChange={(event) => onFromChange(event.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm text-theme-secondarytextcolor">To</span>
          <input
            type="date"
            className="form-control form-control-sm"
            value={to}
            min={from || undefined}
            max={max}
            onChange={(event) => onToChange(event.target.value)}
          />
        </label>
        {active && (
          <button type="button" className="mt-2 text-sm text-primary-600 hover:underline" onClick={clear}>
            Clear dates
          </button>
        )}
      </div>
    </div>
  );
};

export default DateRangeFilter;
